import { LIBELLE_VEHICULE, type DetailPrix } from '../api/prixOpere'
import { fcfa } from './Format'
import { dureeLisible } from '../api/itineraire'

const km = (n: number) => `${n.toFixed(2).replace('.', ',').replace(/,?0+$/, '')} km`
const nombre = (n: number) => String(n).replace('.', ',')

/**
 * La décomposition d'un prix opéré, ligne par ligne : distance, barème (progressif depuis la 0043 : les deux
 * points encadrants), coefficient du véhicule, longue distance, arrondi, majoration « forte demande », supplément
 * hors créneau, total. Sert à l'aperçu avant « Créer la course »
 * (jumeau TypeScript) et au détail d'une course (décomposition posée par la base).
 */
export function BilanPrix({ detail, titreTotal = 'À annoncer au client', dureeS }: { detail: DetailPrix; titreTotal?: string; dureeS?: number | null }) {
  return (
    <div className="bilan-prix">
      <div className="price-line">
        <span>
          {detail.distance_source === 'route' ? (
            <>
              Distance par la route
              <small>à moto{dureeS ? `, environ ${dureeLisible(dureeS)}` : ''}</small>
            </>
          ) : detail.distance_source === 'estimation' ? (
            <>
              Distance estimée<small>itinéraire indisponible : vol d’oiseau × 1,56</small>
            </>
          ) : (
            <>
              Distance estimée<small>vol d’oiseau × 1,3</small>
            </>
          )}
        </span>
        <span className="pl-val">{km(detail.distance_km)}</span>
      </div>
      <div className="price-line">
        <span>
          {detail.mode_bareme === 'progressif' && detail.tranche_depuis_km !== undefined && detail.prix_depuis !== undefined && detail.prix_jusqu !== undefined ? (
            detail.tranche_depuis_km === detail.tranche_jusqu_a_km ? (
              <>Barème : prix à {nombre(detail.tranche_jusqu_a_km)} km (dernier point)</>
            ) : (
              <>
                Barème progressif
                <small>
                  entre {nombre(detail.tranche_depuis_km)} km ({fcfa(detail.prix_depuis)}) et {nombre(detail.tranche_jusqu_a_km)} km ({fcfa(detail.prix_jusqu)})
                  {detail.plancher ? ` — relevé au prix minimum (${fcfa(detail.prix_minimum ?? 0)})` : ''}
                </small>
              </>
            )
          ) : (
            <>Barème : tranche jusqu’à {nombre(detail.tranche_jusqu_a_km)} km</>
          )}
        </span>
        <span className="pl-val">{fcfa(detail.base)}</span>
      </div>
      {detail.coefficient !== 1 && (
        <div className="price-line">
          <span>
            × {LIBELLE_VEHICULE[detail.vehicule].toLowerCase()}
            <small>coefficient {nombre(detail.coefficient)}</small>
          </span>
          <span className="pl-val">{fcfa(detail.base_vehicule)}</span>
        </div>
      )}
      {detail.longue_distance_km > 0 && (
        <div className="price-line sur">
          <span>
            Longue distance : {detail.longue_distance_km} km au-delà de {nombre(detail.seuil_longue_distance_km)} km
            <small>
              {fcfa(detail.prix_km_sup)} / km{detail.option_longue_distance ? ' (option)' : ''}
            </small>
          </span>
          <span className="pl-val">+{fcfa(detail.longue_distance_fcfa)}</span>
        </div>
      )}
      {detail.ajustement_arrondi !== 0 && (
        <div className="price-line">
          <span>
            Arrondi<small>au multiple de {detail.arrondi_fcfa} F</small>
          </span>
          <span className="pl-val">
            {detail.ajustement_arrondi > 0 ? '+' : '−'}
            {fcfa(Math.abs(detail.ajustement_arrondi))}
          </span>
        </div>
      )}
      <LigneTension detail={detail} />
      {detail.supplement_horaire > 0 && (
        <div className="price-line sur">
          <span>Supplément hors créneau</span>
          <span className="pl-val">+{fcfa(detail.supplement_horaire)}</span>
        </div>
      )}
      <div className="price-line total">
        <span>{titreTotal}</span>
        <span className="pl-val">{fcfa(detail.total)}</span>
      </div>
    </div>
  )
}

/**
 * « Forte demande : +X F » (0043) : la majoration, et d'où elle vient (demande / offre dans le rayon). Rien quand la
 * grille n'a pas de tension ; une mention discrète quand le client est au forfait ou que la tension n'a pas pu être lue.
 */
function LigneTension({ detail }: { detail: DetailPrix }) {
  const t = detail.tension
  if (!t || !t.active) return null
  const maj = detail.majoration_tension_fcfa ?? 0
  if (t.exclue === 'forfait') {
    return (
      <div className="price-line">
        <span>
          Forte demande<small>client au forfait : jamais de majoration</small>
        </span>
        <span className="pl-val">—</span>
      </div>
    )
  }
  if (t.exclue === 'non_evaluee') {
    return (
      <div className="price-line">
        <span>
          Forte demande<small>tension non évaluée — la base la compte à la création</small>
        </span>
        <span className="pl-val">?</span>
      </div>
    )
  }
  const livreurs = t.offre ?? 0
  return (
    <div className={maj > 0 ? 'price-line sur' : 'price-line'}>
      <span>
        {maj > 0 ? <>Forte demande : +{fcfa(maj)}</> : 'Forte demande : aucune'}
        <small>
          {t.demande} course{(t.demande ?? 0) > 1 ? 's' : ''} pour {livreurs} livreur{livreurs > 1 ? 's' : ''} disponible{livreurs > 1 ? 's' : ''} à {nombre(t.rayon_km)} km
          {maj > 0 ? ` → +${nombre(t.pct)} %, dont ${fcfa(detail.part_livreur_tension_fcfa ?? 0)} pour le livreur` : ''}
        </small>
      </span>
      <span className="pl-val">{maj > 0 ? `+${fcfa(maj)}` : '—'}</span>
    </div>
  )
}
